import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { fetchOrgsMeta } from './api';
import BulkCreateOrganizations from './BulkCreateOrganizations';
import BulkEditOrganizations from './BulkEditOrganizations';
import ChooseOrganization from './ChooseOrganization';
import CreateOrganization from './CreateOrganization';
import EditOrganization from './EditOrganization';
import CreateTier from '../tiers/CreateTier';
import EditTier from '../tiers/EditTier';
import OrganizationUserManagement from './OrganizationUserManagement';
import { pillActive, pillInactive } from '../styles';
import { useOrderedSubtabs } from '../subtabs';
import { ORGS_SUBTAB_CAPS, OrgsMeta } from './types';
import { TIERS_SUBTAB_CAPS } from '../tiers/types';

type SubtabKey =
  | 'selection'
  | 'create'
  | 'create-bulk'
  | 'edit'
  | 'edit-bulk'
  | 'organization-user-management'
  | 'create-tier'
  | 'edit-tier';

const SUBTABS: { key: SubtabKey; label: string; cap: string }[] = [
  { key: 'selection', label: 'Choose Organization to view', cap: ORGS_SUBTAB_CAPS.selection },
  { key: 'create', label: 'Create Organization', cap: ORGS_SUBTAB_CAPS.create },
  { key: 'create-bulk', label: 'Create Bulk Organizations', cap: ORGS_SUBTAB_CAPS.createBulk },
  { key: 'edit', label: 'Edit Organization', cap: ORGS_SUBTAB_CAPS.edit },
  { key: 'edit-bulk', label: 'Bulk Edit Organizations', cap: ORGS_SUBTAB_CAPS.editBulk },
  {
    key: 'organization-user-management',
    label: 'Organization User Management',
    cap: ORGS_SUBTAB_CAPS.userManagement,
  },
  { key: 'create-tier', label: 'Create Tier', cap: TIERS_SUBTAB_CAPS.create },
  { key: 'edit-tier', label: 'Edit Tier', cap: TIERS_SUBTAB_CAPS.edit },
];

const AdminOrganizationsTab: React.FC = () => {
  const declared = useMemo(() => SUBTABS, []);
  const visibleSubtabs = useOrderedSubtabs('organizations', declared);
  const [active, setActive] = useState<SubtabKey | null>(null);
  const [meta, setMeta] = useState<OrgsMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setActive((current) => {
      if (current && visibleSubtabs.some((t) => t.key === current)) return current;
      return (visibleSubtabs[0]?.key as SubtabKey | undefined) ?? null;
    });
  }, [visibleSubtabs]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchOrgsMeta();
        if (!cancelled) setMeta(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load organization admin data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (visibleSubtabs.length === 0) {
    return (
      <div className="text-sm text-[#8A8A8A]">
        You do not have access to any organization tools in this panel.
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        {visibleSubtabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActive(tab.key)}
            className={`h-8 px-3 rounded-full text-[13px] font-medium transition-colors ${
              active === tab.key ? pillActive : pillInactive
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {loading &&
        active !== 'create-tier' &&
        active !== 'edit-tier' &&
        active !== 'organization-user-management' && (
        <div className="flex items-center gap-2 text-sm text-[#8A8A8A]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}
      {error &&
        !loading &&
        active !== 'create-tier' &&
        active !== 'edit-tier' &&
        active !== 'organization-user-management' && (
        <div className="rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}
      {!loading && !error && meta && active === 'selection' && <ChooseOrganization meta={meta} />}
      {!loading && !error && meta && active === 'create' && <CreateOrganization meta={meta} />}
      {!loading && !error && meta && active === 'create-bulk' && (
        <BulkCreateOrganizations meta={meta} />
      )}
      {!loading && !error && meta && active === 'edit' && <EditOrganization meta={meta} />}
      {!loading && !error && meta && active === 'edit-bulk' && <BulkEditOrganizations meta={meta} />}
      {active === 'organization-user-management' && <OrganizationUserManagement />}
      {active === 'create-tier' && <CreateTier />}
      {active === 'edit-tier' && <EditTier />}
    </div>
  );
};

export default AdminOrganizationsTab;
